import { requestTokenFromHost } from "./request-token-from-host";
import type { EditorToken } from "./types";

/** Refresh at 80% of the token's lifetime — ~12 min on a 15 min token. */
export const PROACTIVE_REFRESH_FRACTION = 0.8;

/**
 * Retry delay after a failed *background* (proactive-timer) refresh. Kept
 * short and fixed rather than backed off — the still-cached token is still
 * being served, so this is a low-stakes retry, not a thundering-herd risk.
 */
export const PROACTIVE_RETRY_DELAY_MS = 30_000;

/** setTimeout's 32-bit ceiling; longer delays overflow and fire immediately. */
export const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export type TokenManagerStatus = "idle" | "loading" | "ready" | "error";

export interface TokenManagerState {
  status: TokenManagerStatus;
  token: EditorToken | null;
  error: Error | null;
}

type Listener = (state: TokenManagerState) => void;

/**
 * Owns the editor token for the lifetime of the app.
 *
 * There must be exactly ONE of these per app instance. Two means two
 * independent proactive-refresh timers and no shared in-flight
 * de-duplication, which is how you end up hammering the host.
 *
 * The token is held in memory only. A reload legitimately loses it and the
 * app re-requests from the host — that is the design, not a bug.
 */
export class TokenManager {
  private state: TokenManagerState = { status: "idle", token: null, error: null };
  private listeners = new Set<Listener>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<EditorToken> | null = null;
  private disposed = false;
  /**
   * When the current token was received, by THIS client's clock.
   *
   * Not `token.expiresAt`: that is the host's wall clock, and the whole
   * scheduling design avoids trusting it (see `scheduleProactiveRefresh`).
   * Comparing `Date.now()` to a value this client recorded itself measures
   * ELAPSED TIME and never needs the two clocks to agree, so it keeps the
   * same skew-immunity while answering a question a timer cannot:
   * "how long has actually passed?"
   */
  private issuedAtMs = 0;

  getState(): TokenManagerState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Call once on app boot.
   *
   * Re-activates a previously-disposed manager. React 18 `<StrictMode>`
   * double-invokes effects (setup → cleanup → setup) without recreating
   * `useMemo` state, so the provider's effect can call dispose() then init()
   * on this SAME instance. Without resetting `disposed` here, that second
   * init()'s refresh would still succeed (state "ready", valid token) but
   * `scheduleProactiveRefresh` would silently no-op forever, degrading to
   * reactive-only refresh. Only init() restores it — a stray getToken()/
   * refresh() call after dispose() must not resurrect the manager.
   *
   * Double-arming can't happen from this: `scheduleProactiveRefresh` clears
   * any existing timer before arming a new one, and if the prior dispose()
   * raced an in-flight refresh, this init()'s `refresh()` coalesces onto the
   * same `inFlight` promise rather than firing a second request.
   */
  async init(): Promise<void> {
    this.disposed = false;
    await this.refresh();
  }

  /**
   * A currently-valid token, fetching one if needed. Concurrent callers share
   * one host round-trip rather than firing duplicates.
   */
  async getToken(): Promise<string> {
    if (this.state.status === "ready" && this.state.token) {
      return this.state.token.value;
    }
    const token = await this.refresh();
    return token.value;
  }

  /**
   * Forces a fresh token. Used by explicit callers (`init()`, `getToken()`
   * when not ready) and the client's reactive 401 path. Failure is
   * user-visible: loading state, then error state + `token: null`, and the
   * rejection propagates to the caller.
   */
  async refresh(): Promise<EditorToken> {
    return this.doRefresh(false);
  }

  /**
   * Shared refresh implementation. `background` distinguishes the
   * proactive-timer-initiated path (and its retries) from every other
   * caller:
   *  - foreground (`background: false`): transitions through "loading", and
   *    on failure flips to "error" with `token: null`, rethrowing.
   *  - background (`background: true`): never touches `state` on failure —
   *    the still-cached token keeps being served by `getToken()`'s ready
   *    fast-path (its real expiry is guarded by the client's reactive 401
   *    backstop). Instead it re-arms a `PROACTIVE_RETRY_DELAY_MS` retry.
   *
   * Coalescing edge case: if a background refresh is already in flight when
   * a foreground caller arrives, both share this same promise (`inFlight`).
   * That promise's `background` flag is fixed to `true` at the point it was
   * created, so if it fails the foreground caller still sees the rejection
   * (the `throw error` below is unconditional) even though `state` is left
   * untouched — i.e. a foreground `await` can reject while `getState()`
   * stays "ready". Callers needing guaranteed error-state semantics should
   * rely on `getState()`, not just catching `refresh()`/`getToken()`.
   */
  private async doRefresh(background: boolean): Promise<EditorToken> {
    if (this.inFlight) return this.inFlight;

    if (!background) {
      this.setState({ status: "loading", token: this.state.token, error: null });
    }

    this.inFlight = requestTokenFromHost()
      .then((token) => {
        this.issuedAtMs = Date.now();
        this.setState({ status: "ready", token, error: null });
        this.scheduleProactiveRefresh(token);
        return token;
      })
      .catch((error: Error) => {
        if (background) {
          this.scheduleRetry();
        } else {
          this.setState({ status: "error", token: null, error });
        }
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
      });

    return this.inFlight;
  }

  dispose(): void {
    this.disposed = true;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  /**
   * Refresh now IF enough real time has passed, otherwise do nothing.
   *
   * WHY A TIMER IS NOT ENOUGH. `scheduleProactiveRefresh` is the happy path
   * and stays the happy path, but `setTimeout` does not run while a machine
   * sleeps, and browsers throttle timers hard in hidden tabs — while the wall
   * clock keeps going. A merchant who leaves the editor open and comes back a
   * quarter of an hour later has a token that expired during a timer that
   * never fired. Their next click 401s, and if that reactive refresh cannot
   * complete they get the terminal reload screen (Milan, 2026-10-06).
   *
   * So the host-visibility events call this on the way back in, and the gap
   * is measured rather than assumed.
   *
   * BACKGROUND on purpose. A failure here must not flip the app to "error"
   * and throw the merchant out — the cached token may still work, and the
   * client's 401 path is still the backstop. Same reasoning as the
   * proactive timer's own refresh.
   */
  refreshIfStale(): void {
    if (this.disposed) return;
    // Nothing issued yet (or an error state): `init()`/the 401 path own that.
    if (this.state.status !== "ready" || !this.state.token) return;
    const lifetimeMs = this.state.token.expiresInSec * 1000;
    if (!Number.isFinite(lifetimeMs) || lifetimeMs <= 0) return;
    // A clock that jumped BACKWARDS (NTP correction, manual change) gives a
    // negative gap, which is already below the threshold — so this one
    // comparison covers it, and an explicit `< 0` guard would be a branch no
    // test could ever distinguish.
    const elapsedMs = Date.now() - this.issuedAtMs;
    if (elapsedMs < lifetimeMs * PROACTIVE_REFRESH_FRACTION) return;
    this.doRefresh(true).catch(() => {});
  }

  private scheduleProactiveRefresh(token: EditorToken): void {
    if (this.disposed) return;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Relative on purpose (clock-skew immunity), and clamped: a non-finite or
    // non-positive lifetime must NOT become setTimeout(cb, NaN) — which fires
    // immediately, and every refresh would instantly schedule the next: a
    // self-sustaining storm against the host. No timer at all is safe — the
    // API client's reactive 401 path is the backstop.
    const rawDelayMs = token.expiresInSec * PROACTIVE_REFRESH_FRACTION * 1000;
    if (!Number.isFinite(rawDelayMs) || rawDelayMs <= 0) return;
    const delayMs = Math.min(Math.max(rawDelayMs, 1_000), MAX_TIMEOUT_MS);
    this.refreshTimer = setTimeout(() => {
      this.doRefresh(true).catch(() => {});
    }, delayMs);
  }

  private scheduleRetry(): void {
    if (this.disposed) return;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.doRefresh(true).catch(() => {});
    }, PROACTIVE_RETRY_DELAY_MS);
  }

  private setState(next: TokenManagerState): void {
    this.state = next;
    this.listeners.forEach((listener) => listener(next));
  }
}
